import React, { useEffect } from 'react';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { muted, primary, READY_CLS, isOverdue } from './mbApi';
import { STAGE_LABEL, usePicName } from './HandoffControl';
import { taskData, TaskSummary, TaskMaterials, TaskTests, TaskLearning } from './TaskSections';

export const TASK_TABS = [['ringkasan', 'Ringkasan'], ['bahan', 'Bahan'], ['ujian', 'Ujian & Hasil'], ['belajar', 'Pembelajaran']];

const Header = ({ w }) => {
  const picName = usePicName();
  const r = w.readiness || {};
  return (
    <SheetHeader className="sticky top-0 z-10 bg-[hsl(var(--background))] border-b border-[hsl(var(--border))] p-4 pr-14 text-left space-y-1">
      <SheetClose className="absolute right-3 top-3 min-w-10 min-h-10 inline-flex items-center justify-center rounded-md hover:bg-white/10" aria-label="Tutup butiran task" data-testid="mb-drawer-close">
        <X size={18} />
      </SheetClose>
      <SheetTitle className={`text-base ${primary} flex flex-wrap items-center gap-2`}>
        {w.code && <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-[#F77F00]/15 text-[#F77F00]">{w.code}</span>}
        <span className="min-w-0 break-words" data-testid="mb-drawer-title">{w.title}</span>
        {w.archived && <span className="text-xs px-1.5 rounded bg-white/10">Arkib</span>}
      </SheetTitle>
      <SheetDescription asChild>
        <div className="text-sm space-y-0.5">
          <p data-testid="mb-drawer-status">
            <span className={muted}>Tahap:</span> <span className={primary}>{STAGE_LABEL[w.current_stage || 'brief']}</span>
            {' · '}<span className={muted}>PIC:</span> <span className={primary}>{picName(w.current_pic_id)}</span>
            {w.deadline && <span className={isOverdue(w) ? 'text-red-400' : muted}> · {isOverdue(w) ? 'Lewat — ' : ''}due {w.deadline}</span>}
          </p>
          <p className={READY_CLS(r)} data-testid="mb-drawer-readiness">
            {r.label}{r.reason ? ` — ${r.reason}` : ''}{!r.ok && r.fixer_id ? ` · boleh dibetulkan oleh ${picName(r.fixer_id)}` : ''}
          </p>
        </div>
      </SheetDescription>
    </SheetHeader>
  );
};

export const TaskDrawer = ({ w, tab, setTab, focusId, onClose, ibId, board, dash, caps, showArchived, reload, simulate }) => {
  useEffect(() => {
    if (!w || !focusId) return undefined;
    const id = setTimeout(() => {
      const el = document.querySelector(`[data-testid="mb-creative-${focusId}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-[#F77F00]');
      setTimeout(() => el.classList.remove('ring-2', 'ring-[#F77F00]'), 2500);
    }, 250);
    return () => clearTimeout(id);
  }, [w, tab, focusId]);
  const d = w ? taskData(w, board, dash, showArchived) : null;
  const p = { ibId, w, d, caps, board, reload, simulate };
  return (
    <Sheet open={!!w} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="mb-scope w-full sm:max-w-3xl p-0 overflow-y-auto" data-testid="mb-task-drawer">
        {w && (
          <>
            <Header w={w} />
            <Tabs value={tab} onValueChange={setTab} className="p-4" data-testid={`mb-versions-${w.id}`}>
              <TabsList className="flex-wrap h-auto w-full justify-start">
                {TASK_TABS.map(([k, l]) => <TabsTrigger key={k} value={k} className="min-h-10 text-sm" data-testid={`mb-drawer-tab-${k}`}>{l}</TabsTrigger>)}
              </TabsList>
              <TabsContent value="ringkasan" className="mt-4"><TaskSummary {...p} /></TabsContent>
              <TabsContent value="bahan" className="mt-4"><TaskMaterials {...p} /></TabsContent>
              <TabsContent value="ujian" className="mt-4"><TaskTests {...p} /></TabsContent>
              <TabsContent value="belajar" className="mt-4"><TaskLearning {...p} /></TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
